import { getConfig } from '@/server';
import {
  Router,
  type Request,
  type Response,
  type NextFunction,
  type Router as ExpressRouter,
} from 'express';
import {
  getRedirectUri,
  handleOAuthUserAuthentication,
  handleOAuthProviderLink,
  validateOAuthCode,
  type OAuthUserData,
  clearOAuthLinkCookie,
  validateOAuthStateAndGetMode,
  sendOAuthError,
  prepareOAuthInitiation,
  resolveMobileOutcomeFromCookie,
  toOAuthOutcome,
} from './oauthCommon';

interface GoogleTokenResponse {
  access_token: string;
  expires_in: number;
  scope: string;
  token_type: string;
  id_token: string;
}

interface GoogleUserInfo {
  id: string;
  name: string;
  given_name?: string;
  family_name?: string;
  email: string;
  verified_email: boolean;
  picture?: string;
}

async function exchangeCodeForToken(
  code: string,
  clientId: string,
  clientSecret: string,
  redirectUri: string
): Promise<GoogleTokenResponse> {
  const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      code,
      client_id: clientId,
      client_secret: clientSecret,
      redirect_uri: redirectUri,
      grant_type: 'authorization_code',
    }),
  });

  if (!tokenResponse.ok) {
    throw new Error(`Failed to exchange code for token: ${tokenResponse.statusText}`);
  }

  return tokenResponse.json();
}

async function fetchGoogleUserInfo(accessToken: string): Promise<GoogleUserInfo> {
  const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!userInfoResponse.ok) {
    throw new Error(`Failed to fetch user info: ${userInfoResponse.statusText}`);
  }

  return userInfoResponse.json();
}

async function handleGoogleAuthenticationCallback(req: Request, res: Response) {
  const code = validateOAuthCode(req.query.code);

  if (!code) {
    // No usable state to validate yet (a declined consent screen sends none),
    // but the state cookie still records where the app lives, so this can be
    // reported into the app instead of as JSON in the device browser.
    sendOAuthError(
      res,
      400,
      'Missing authorization code',
      resolveMobileOutcomeFromCookie(req, 'authStateGoogle') ?? undefined,
      'missing_code'
    );
    return;
  }

  const stateResult = validateOAuthStateAndGetMode(req, res, 'authStateGoogle');
  if (!stateResult) return;
  const { mode, linkedUserId } = stateResult;
  const outcome = toOAuthOutcome(stateResult);

  const googleClientId = String(getConfig('_system.user.auth.google.clientId'));
  const googleClientSecret = String(getConfig('_system.user.auth.google.clientSecret'));
  const redirectUri = getRedirectUri('google');

  try {
    // Exchange code for tokens
    const tokenData = await exchangeCodeForToken(
      code,
      googleClientId,
      googleClientSecret,
      redirectUri
    );

    // Fetch user info
    const googleUser = await fetchGoogleUserInfo(tokenData.access_token);

    const userData: OAuthUserData = {
      id: googleUser.id,
      email: googleUser.email,
      emailVerified: googleUser.verified_email,
      providerName: 'google',
      firstName: googleUser.given_name || undefined,
      lastName: googleUser.family_name || undefined,
      avatarUrl: googleUser.picture || undefined,
    };
    if (mode === 'link') {
      await handleOAuthProviderLink(req, res, userData, linkedUserId, outcome);
    } else {
      await handleOAuthUserAuthentication(req, res, userData, outcome);
    }
  } catch (error) {
    console.error('Google OAuth error:', error);
    if (mode === 'link') {
      clearOAuthLinkCookie(res);
    }
    sendOAuthError(res, 500, 'Authentication failed', outcome);
  }
}

function getRouter(): ExpressRouter {
  const googleAuthRouter = Router();

  // Middleware to check if Google auth is enabled and configured
  const checkGoogleEnabled = (_req: Request, res: Response, next: NextFunction) => {
    const googleEnabled = Boolean(getConfig('_system.user.auth.google.enabled'));
    const googleClientId = String(getConfig('_system.user.auth.google.clientId'));
    const googleClientSecret = String(getConfig('_system.user.auth.google.clientSecret'));

    if (!googleEnabled || !googleClientId || !googleClientSecret) {
      sendOAuthError(res, 503, 'Google authentication is not configured', {
        platform: 'api',
      });
      return;
    }

    next();
  };

  // Initiate OAuth flow
  googleAuthRouter.get(
    '/api/_internal/auth/google',
    checkGoogleEnabled,
    async (req: Request, res: Response) => {
      const googleClientId = String(getConfig('_system.user.auth.google.clientId'));
      const redirectUri = getRedirectUri('google');

      const initiation = await prepareOAuthInitiation(req, res, 'authStateGoogle');
      if (!initiation) return;
      const { state } = initiation;

      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.append('client_id', googleClientId);
      authUrl.searchParams.append('redirect_uri', redirectUri);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('scope', 'profile email');
      authUrl.searchParams.append('access_type', 'online');
      authUrl.searchParams.append('state', state);

      res.redirect(authUrl.toString());
    }
  );

  // Handle OAuth callback
  googleAuthRouter.get(
    '/api/_internal/auth/google/callback',
    checkGoogleEnabled,
    handleGoogleAuthenticationCallback
  );

  return googleAuthRouter;
}

export default getRouter;
